import ActionBarItem from '@rio-cloud/rio-uikit/ActionBarItem';
import ReleaseNotesDialog from '@rio-cloud/rio-uikit/ReleaseNotesDialog';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { config } from '../config';
import { LOCALE_PLACEHOLDER, replaceLocalePlaceholder } from './app/config/productConfig';
import customerCenterReleaseNotes from './customerCenterReleaseNotes.json';

export const CUSTOMER_CENTER_RELEASE_NOTES_VERSION = config.serviceVersion;
export const CUSTOMER_CENTER_RELEASE_NOTES_STORAGE_KEY = 'customer-center-release-notes-version';
export const FEEDBACK_URL = 'https://contact-form.rio.cloud/contact?reason=feedback';
const FAQ_URL_TEMPLATE = `https://rio.cloud/${LOCALE_PLACEHOLDER}/faqs`;

export const InformationActionBarItem = () => {
    const [showReleaseNotes, setShowReleaseNotes] = useState(false);
    const [hasUnreadReleaseNotes, setHasUnreadReleaseNotes] = useState(
        () =>
            window.localStorage.getItem(CUSTOMER_CENTER_RELEASE_NOTES_STORAGE_KEY) !==
            CUSTOMER_CENTER_RELEASE_NOTES_VERSION
    );
    const intl = useIntl();
    const moduleName = intl.formatMessage({ id: 'intl-msg:marketplace.customerCenter' });
    const whatsNewTitle = intl.formatMessage({ id: 'intl-msg:marketplace.customerCenter.info.whatsNew' });
    const prefersReducedMotion =
        typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const faqUrl = replaceLocalePlaceholder(FAQ_URL_TEMPLATE, intl.locale);
    const translatedReleaseNotes = Object.fromEntries(
        Object.entries(customerCenterReleaseNotes).map(([version, releaseNote]) => [
            version,
            {
                date: intl.formatDate(new Date(`${releaseNote.date}T00:00:00.000Z`), {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    timeZone: 'UTC',
                }),
                content: releaseNote.items.map(item => (
                    <FormattedMessage key={item.id} id={item.id} defaultMessage={item.defaultMessage} />
                )),
            },
        ])
    );
    const popoverTitle = (
        <div>
            <span>{moduleName}</span>
            <span className='text-color-gray margin-left-10'>{CUSTOMER_CENTER_RELEASE_NOTES_VERSION}</span>
        </div>
    );
    const openReleaseNotes = () => {
        window.localStorage.setItem(CUSTOMER_CENTER_RELEASE_NOTES_STORAGE_KEY, CUSTOMER_CENTER_RELEASE_NOTES_VERSION);
        setHasUnreadReleaseNotes(false);
        setShowReleaseNotes(true);
    };
    const unreadReleaseNotesBadge = (className: string) => (
        <span
            className={`${className}${prefersReducedMotion ? '' : ' badge-indicator-pinging'}`}
            role='status'
            aria-label={whatsNewTitle}
        />
    );

    return (
        <>
            <ActionBarItem id='customer-center-information' popoverWidth={300}>
                <ActionBarItem.Icon title={moduleName}>
                    <span className='icon uikit-icon icon-info-sign' />
                    {hasUnreadReleaseNotes && unreadReleaseNotesBadge('badge badge-primary badge-indicator')}
                </ActionBarItem.Icon>
                <ActionBarItem.Popover title={popoverTitle}>
                    <ActionBarItem.List>
                        <ActionBarItem.ListItem iconName='document' onClick={openReleaseNotes}>
                            {whatsNewTitle}
                            {hasUnreadReleaseNotes &&
                                unreadReleaseNotesBadge('badge badge-primary margin-left-5 scale-80')}
                        </ActionBarItem.ListItem>
                        <ActionBarItem.ListItem iconName='icon-question-sign' hasLink>
                            <a href={faqUrl} target='_blank' rel='noreferrer'>
                                FAQ
                            </a>
                        </ActionBarItem.ListItem>
                        <ActionBarItem.ListItem iconName='send' hasLink>
                            <a href={FEEDBACK_URL} target='_blank' rel='noreferrer'>
                                <FormattedMessage id='intl-msg:marketplace.customerCenter.info.feedback' />
                            </a>
                        </ActionBarItem.ListItem>
                    </ActionBarItem.List>
                </ActionBarItem.Popover>
            </ActionBarItem>
            <ReleaseNotesDialog
                showReleaseNotes={showReleaseNotes}
                setShowReleaseNotes={setShowReleaseNotes}
                moduleName={moduleName}
                whatsNewTitle={whatsNewTitle}
                closeButtonText={intl.formatMessage({ id: 'intl-msg:marketplace.customerCenter.info.close' })}
                translatedReleaseNotes={translatedReleaseNotes}
                currentVersion={CUSTOMER_CENTER_RELEASE_NOTES_VERSION}
                RELEASE_NOTES_ITEM={CUSTOMER_CENTER_RELEASE_NOTES_STORAGE_KEY}
            />
        </>
    );
};
